import {
  Alert,
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Col,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { CheckCircleOutlined, EyeOutlined, MailOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet-async';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiCall } from '../../API/RestApi';
import { modalError } from '../../Component/Modal/ModalLayout';
import { WarningLetterAttachmentList } from './WarningLetterAttachments';

const { TextArea } = Input;
const { Text } = Typography;

const STATUS_COLORS = { sent: 'blue', acknowledged: 'green', cancelled: 'red' };
const formatDate = (value, withTime = false) => value ? moment(value).format(withTime ? 'DD-MM-YYYY HH:mm:ss' : 'DD-MM-YYYY') : '-';

function WarningLetterEmployee() {
  const token = useSelector((state) => state.user.token);
  const [ackForm] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState();
  const [page, setPage] = useState({ current: 1, pageSize: 10, total: 0 });
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);
  const [alert, setAlert] = useState(null);
  const authHeaders = useMemo(() => ({ Authorization: 'Bearer ' + token }), [token]);

  const request = async (method, url, payload) => {
    const res = await apiCall({ method, url, data: { data: payload || '', headers: authHeaders } });
    if (!res || res.status !== 'success') throw new Error(res?.message || 'Terjadi kesalahan saat memproses data');
    return res;
  };

  const loadData = async (nextPage = page.current, nextPageSize = page.pageSize) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: nextPage, limit: nextPageSize });
      if (status) params.set('status', status);
      const res = await request('GET', `/warning-letters/me?${params.toString()}`);
      const payload = res.data || {};
      setData(payload.data || []);
      setPage({ current: payload.page || nextPage, pageSize: payload.limit || nextPageSize, total: payload.total_data || 0 });
    } catch (e) {
      modalError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1, page.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const openDetail = async (record) => {
    setLoading(true);
    try {
      const res = await request('GET', `/warning-letters/me/${record.warning_letter_id}`);
      setDetail(res.data);
      setDetailOpen(true);
      setData((current) => current.map((item) => item.warning_letter_id === record.warning_letter_id ? { ...item, read_at: res.data.read_at } : item));
    } catch (e) {
      modalError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openAcknowledge = (record) => {
    setDetail(record);
    ackForm.resetFields();
    setAckOpen(true);
  };

  const submitAcknowledge = async (values) => {
    setLoading(true);
    try {
      const res = await request('POST', `/warning-letters/me/${detail.warning_letter_id}/acknowledge`, {
        employee_response: values.employee_response || '',
      });
      setAlert(res.message);
      setAckOpen(false);
      ackForm.resetFields();
      await loadData();
      if (detailOpen) {
        const detailRes = await request('GET', `/warning-letters/me/${detail.warning_letter_id}`);
        setDetail(detailRes.data);
      }
    } catch (e) {
      modalError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'No', width: 60, render: (_, __, index) => (page.current - 1) * page.pageSize + index + 1 },
    { title: 'No. Surat', dataIndex: 'letter_number', key: 'letter_number' },
    { title: 'Level', dataIndex: 'warning_level', key: 'warning_level', width: 90 },
    { title: 'Perihal', dataIndex: 'subject', key: 'subject' },
    { title: 'Tanggal Pelanggaran', dataIndex: 'violation_date', key: 'violation_date', render: (value) => formatDate(value) },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 130,
      render: (value) => <Tag color={STATUS_COLORS[value] || 'default'}>{String(value || '-').toUpperCase()}</Tag>,
    },
    {
      title: 'Dibaca', dataIndex: 'read_at', key: 'read_at', width: 110,
      render: (value) => value ? <Tag color="green">Sudah</Tag> : <Tag>Belum</Tag>,
    },
    {
      title: 'Aksi', key: 'action', width: 150,
      render: (_, record) => (
        <Space className="warning-letter-actions">
          <Tooltip title="Buka surat"><Button icon={<EyeOutlined />} onClick={() => openDetail(record)} /></Tooltip>
          {record.status === 'sent' && !record.acknowledged_at && (
            <Tooltip title="Konfirmasi penerimaan"><Button type="primary" icon={<CheckCircleOutlined />} onClick={() => openAcknowledge(record)} /></Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="warning-letter-page">
      <Helmet><title>Surat Teguran Saya</title></Helmet>
      {alert && <Alert message={alert} type="success" showIcon closable style={{ marginBottom: 16 }} afterClose={() => setAlert(null)} />}
      <Row className="warning-letter-toolbar" justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col><h3 style={{ margin: 0 }}><MailOutlined /> Surat Teguran Saya</h3></Col>
        <Col xs={24} sm={8} md={5}>
          <Select placeholder="Semua status" allowClear style={{ width: '100%' }} onChange={setStatus}
            options={[{ value: 'sent', label: 'Terkirim' }, { value: 'acknowledged', label: 'Sudah diterima' }, { value: 'cancelled', label: 'Dibatalkan' }]} />
        </Col>
      </Row>

      <Table className="responsive-data-table" size="small" rowKey="warning_letter_id" loading={loading} columns={columns} dataSource={data} scroll={{ x: 900 }} pagination={page} onChange={(pagination) => loadData(pagination.current, pagination.pageSize)} />

      <Modal
        title="Surat Teguran"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        width="min(850px, calc(100vw - 24px))"
        styles={{ body: { maxHeight: '72vh', overflowY: 'auto', overflowX: 'auto', paddingBottom: 12 } }}
        footer={detail ? (
          <Space>
            <Button onClick={() => setDetailOpen(false)}>Tutup</Button>
            {detail.status === 'sent' && !detail.acknowledged_at && <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => openAcknowledge(detail)}>Konfirmasi Penerimaan</Button>}
          </Space>
        ) : null}
      >
        {detail && (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 1080 }}>
            <Descriptions bordered column={{ xs: 1, sm: 1, md: 2 }} size="small" styles={{ content: { whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }, label: { whiteSpace: 'normal' } }}>
              <Descriptions.Item label="Nomor Surat">{detail.letter_number || '-'}</Descriptions.Item>
              <Descriptions.Item label="Status"><Tag color={STATUS_COLORS[detail.status] || 'default'}>{String(detail.status || '-').toUpperCase()}</Tag></Descriptions.Item>
              <Descriptions.Item label="Level Teguran">{detail.warning_level || '-'}</Descriptions.Item>
              <Descriptions.Item label="Tanggal Pelanggaran">{formatDate(detail.violation_date)}</Descriptions.Item>
              <Descriptions.Item label="Perihal" span={2}>{detail.subject || '-'}</Descriptions.Item>
              <Descriptions.Item label="Pelanggaran" span={2}>{detail.violation || '-'}</Descriptions.Item>
              <Descriptions.Item label="Tindakan Perbaikan" span={2}>{detail.corrective_action || '-'}</Descriptions.Item>
              <Descriptions.Item label="Tanggal Berlaku">{formatDate(detail.effective_date)}</Descriptions.Item>
              <Descriptions.Item label="Berlaku Sampai">{formatDate(detail.valid_until)}</Descriptions.Item>
              <Descriptions.Item label="Catatan" span={2}>{detail.notes || '-'}</Descriptions.Item>
              <Descriptions.Item label="Attachment" span={2}><WarningLetterAttachmentList attachments={detail.attachments || []} /></Descriptions.Item>
              <Descriptions.Item label="Dibuat Oleh">{detail.created_by_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Dikirim Pada">{formatDate(detail.sent_at, true)}</Descriptions.Item>
              <Descriptions.Item label="Dibaca Pada">{formatDate(detail.read_at, true)}</Descriptions.Item>
              <Descriptions.Item label="Diterima Pada">{formatDate(detail.acknowledged_at, true)}</Descriptions.Item>
              {detail.employee_response && <Descriptions.Item label="Tanggapan Saya" span={2}>{detail.employee_response}</Descriptions.Item>}
              {detail.status === 'cancelled' && <Descriptions.Item label="Alasan Pembatalan" span={2}>{detail.cancellation_reason || '-'}</Descriptions.Item>}
            </Descriptions>
            </div>
            {detail.status === 'sent' && !detail.acknowledged_at && <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>Setelah membaca surat, gunakan tombol Konfirmasi Penerimaan untuk mencatat bahwa surat telah diterima.</Text>}
          </div>
        )}
      </Modal>

      <Modal title="Konfirmasi Penerimaan Surat" open={ackOpen} onCancel={() => setAckOpen(false)} onOk={() => ackForm.submit()} okText="Konfirmasi" confirmLoading={loading}>
        <Form form={ackForm} layout="vertical" onFinish={submitAcknowledge}>
          <Form.Item name="employee_response" label="Tanggapan (opsional)"><TextArea rows={5} placeholder="Contoh: Saya menerima surat ini dan akan melakukan perbaikan sesuai arahan." /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default WarningLetterEmployee;
